import React, { useState, useEffect, useContext } from 'react';
import { 
  doc, getDoc, updateDoc, arrayUnion, runTransaction 
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faUpload, faFilePdf, faFileImage, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './AddEquipmentModal.module.css';

const AddVisualEquipmentModal = ({ isOpen, onClose, onSave, editItem }) => {
  const { userData } = useContext(UserContext);
  
  // Form state
  const [type, setType] = useState('');
  const [subType, setSubType] = useState('');
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [rentPerDay, setRentPerDay] = useState('');
  
  // File upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  
  // Constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_DOC_SIZE = 900 * 1024; // 900KB for Firestore document
  
  // Visual equipment options
  const typeOptions = [
    'Projectors',
    'Screens',
    'LED Walls',
    'Cameras',
    'Video Mixers',
    'Media Servers',
    'Displays',
    'Other Visual Equipment'
  ];
  
  const subTypeOptions = {
    'Projectors': ['DLP', 'LCD', 'LED', 'Short Throw', 'Ultra Short Throw'],
    'Screens': ['Fixed Frame', 'Portable', 'Inflatable', 'Rear Projection'],
    'LED Walls': ['Indoor', 'Outdoor', 'Flexible', 'Transparent'],
    'Cameras': ['DSLR', 'Mirrorless', 'Video Cameras', 'PTZ Cameras'],
    'Video Mixers': ['Analog', 'Digital', 'Hybrid'],
    'Media Servers': ['Hardware-based', 'Software-based'],
    'Displays': ['LCD', 'OLED', 'Plasma', 'Touchscreen'],
    'Other Visual Equipment': ['Video Scalers', 'Signal Converters', 'Video Processors']
  };
  
  const brandOptions = {
    'Projectors': ['Epson', 'Sony', 'Panasonic', 'Barco', 'Christie', 'NEC', 'Other'],
    'Screens': ['Da-Lite', 'Draper', 'Elite Screens', 'Screen Innovations', 'Stewart Filmscreen', 'Other'],
    'LED Walls': ['Absen', 'Unilumin', 'Roe Visual', 'Leyard', 'Daktronics', 'Other'],
    'Cameras': ['Sony', 'Panasonic', 'Canon', 'Blackmagic Design', 'Red Digital Cinema', 'Other'],
    'Video Mixers': ['Blackmagic Design', 'Roland', 'Panasonic', 'NewTek', 'Datavideo', 'Other'],
    'Media Servers': ['Resolume', 'Green Hippo', 'Disguise', 'Watchout', 'Arkaos', 'Other'],
    'Displays': ['Samsung', 'LG', 'Sony', 'NEC', 'Panasonic', 'Other'],
    'Other Visual Equipment': ['AJA', 'Blackmagic Design', 'Kramer', 'Extron', 'Crestron', 'Other']
  };
  
  // Initialize form with edit item data
  useEffect(() => {
    if (editItem) {
      setType(editItem.type || '');
      setSubType(editItem.subType || '');
      setBrand(editItem.brand || '');
      setName(editItem.name || '');
      setDescription(editItem.description || '');
      setQuantity(editItem.quantity?.toString() || '');
      setRentPerDay(editItem.rentPerDay?.toString() || '');
      
      // Set photo preview if exists
      if (editItem.photo) {
        setPhotoPreview(editItem.photo);
      }
      
      // Set attachment name if exists
      if (editItem.fileUrl) {
        // Extract filename from URL
        const filename = editItem.fileUrl.substring(editItem.fileUrl.lastIndexOf('/') + 1);
        setAttachmentName(filename);
      }
    }
  }, [editItem]);
  
  // Handle file selection for image
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFormErrors({
        ...formErrors,
        photo: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`
      });
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFormErrors({
        ...formErrors,
        photo: 'Invalid file type. Please upload an image.'
      });
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setPhotoFile(file);
    setFormErrors({ ...formErrors, photo: null });
  };
  
  // Handle file selection for attachment
  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFormErrors({
        ...formErrors,
        attachment: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`
      });
      return;
    }
    
    // Validate file type (PDF only)
    if (file.type !== 'application/pdf') {
      setFormErrors({
        ...formErrors,
        attachment: 'Invalid file type. Please upload a PDF.'
      });
      return;
    }
    
    setAttachmentFile(file);
    setAttachmentName(file.name);
    setFormErrors({ ...formErrors, attachment: null });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    let errors = {};
    if (!name) errors.name = 'Name is required';
    if (!type) errors.type = 'Type is required';
    if (!subType) errors.subType = 'Sub Type is required';
    if (!brand) errors.brand = 'Brand is required';
    if (!quantity) errors.quantity = 'Quantity is required';
    if (!rentPerDay) errors.rentPerDay = 'Rent per day is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsUploading(true);
    try {
      const db = getFirestoreInstance();
      
      // Generate a unique ID if new item
      const itemId = editItem ? editItem.id : Date.now().toString();
      
      // URLs for files
      let photoUrl = editItem ? editItem.photo : null;
      let fileUrl = editItem ? editItem.fileUrl : null;
      
      // Upload photo if new or changed
      if (photoFile) {
        // Delete previous photo if exists
        if (editItem && editItem.photo) {
          try {
            const storage = getStorage();
            const photoRef = ref(storage, editItem.photo);
            await deleteObject(photoRef);
          } catch (error) {
            console.error('Error deleting previous photo:', error);
          }
        }
        
        // Upload new photo
        photoUrl = await uploadMediaToBackblaze(photoFile, 'image', itemId);
      }
      
      // Upload attachment if new or changed
      if (attachmentFile) {
        // Delete previous attachment if exists
        if (editItem && editItem.fileUrl) {
          try {
            const storage = getStorage();
            const fileRef = ref(storage, editItem.fileUrl);
            await deleteObject(fileRef);
          } catch (error) {
            console.error('Error deleting previous attachment:', error);
          }
        }
        
        // Upload new attachment
        fileUrl = await uploadMediaToBackblaze(attachmentFile, 'attachment', itemId);
      }
      
      // Create or update item object
      const equipmentItem = {
        id: itemId,
        type,
        subType,
        brand,
        name,
        description: description || '',
        quantity: parseInt(quantity),
        rentPerDay: parseFloat(rentPerDay),
        photo: photoUrl,
        fileUrl: fileUrl,
        updatedAt: new Date().toISOString()
      };
      
      if (!editItem) {
        // Add new item
        await addInventoryItem(db, 'visualEquipment', equipmentItem);
        onSave(equipmentItem, 'visualEquipment');
      } else {
        // Update existing item
        await updateInventoryItem(db, 'visualEquipment', equipmentItem);
        onSave(equipmentItem, 'visualEquipment');
      }
    } catch (error) {
      console.error('Error saving visual equipment:', error);
      setFormErrors({
        ...formErrors,
        submit: `Failed to ${editItem ? 'update' : 'save'} equipment. Please try again.`
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  // Upload file to Backblaze through API
  const uploadMediaToBackblaze = async (file, type, itemId) => {
    try {
      setUploadProgress(0);
      
      // Create FormData
      const formData = new FormData();
      formData.append('media', file);
      formData.append('type', type);
      
      // Include metadata
      const queryParams = new URLSearchParams({
        context: 'inventory',
        category: 'visualEquipment',
        itemId: itemId
      }).toString();
      
      // Get auth token from UserContext or localStorage
      const authToken = localStorage.getItem('authToken');
      
      // Make the request to upload endpoint
      const response = await fetch(`/api/processAndUploadMedia?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }
      
      const responseData = await response.json();
      
      // Get file URL from response
      let url;
      if (responseData.files && responseData.files.length > 0) {
        url = responseData.files[0].url;
      } else {
        url = responseData.url;
      }
      
      setUploadProgress(100);
      return url;
    } catch (error) {
      console.error('Error uploading to Backblaze:', error);
      throw error;
    }
  };
  
  // Add inventory item to Firestore
  const addInventoryItem = async (db, category, item) => {
    return await runTransaction(db, async (transaction) => {
      let docNumber = 1;
      let docRef = doc(db, 'users', userData.userId, 'inventory', `${category}_${docNumber}`);
      let docSnap = await transaction.get(docRef);
      
      while (docSnap.exists()) {
        const data = docSnap.data();
        const currentSize = JSON.stringify(data).length;
        const newItemSize = JSON.stringify(item).length;
        
        if (currentSize + newItemSize < MAX_DOC_SIZE) {
          // Update existing document
          transaction.update(docRef, {
            items: arrayUnion(item)
          });
          return;
        } else {
          // Move to next document
          docNumber++;
          docRef = doc(db, 'users', userData.userId, 'inventory', `${category}_${docNumber}`);
          docSnap = await transaction.get(docRef);
        }
      }
      
      // Create new document if doesn't exist
      transaction.set(docRef, {
        items: [item]
      });
      
      // Update lastUpdated timestamp in user document
      const userDocRef = doc(db, 'users', userData.userId);
      transaction.update(userDocRef, {
        'inventory.lastUpdated': new Date()
      });
    });
  };
  
  // Update inventory item in Firestore
  const updateInventoryItem = async (db, category, updatedItem) => {
    // Find the document containing the item
    let docNumber = 1;
    let itemFound = false;
    let docRef;
    
    while (!itemFound) {
      docRef = doc(db, 'users', userData.userId, 'inventory', `${category}_${docNumber}`);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.items && data.items.some(i => i.id === updatedItem.id)) {
          itemFound = true;
          
          // Update the document
          await runTransaction(db, async (transaction) => {
            // Get the latest version of the document
            const latestDocSnap = await transaction.get(docRef);
            const latestData = latestDocSnap.data();
            
            // Create a new array with the updated item
            const updatedItems = latestData.items.map(item => 
              item.id === updatedItem.id ? updatedItem : item
            );
            
            // Update the document
            transaction.update(docRef, { items: updatedItems });
            
            // Update lastUpdated timestamp in user document
            const userDocRef = doc(db, 'users', userData.userId);
            transaction.update(userDocRef, {
              'inventory.lastUpdated': new Date()
            });
          });
        } else {
          docNumber++;
        }
      } else {
        break; // Item not found
      }
    }
    
    if (!itemFound) {
      throw new Error('Item not found in any document');
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <h2 className={styles.modalTitle}>
          {editItem ? 'Edit Visual Equipment' : 'Add Visual Equipment'}
        </h2>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Photo Section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Equipment Photo</h3>
            <div className={styles.photoUploadSection}>
              {photoPreview ? (
                <div className={styles.photoPreviewContainer}>
                  <img 
                    src={photoPreview} 
                    alt="Equipment preview" 
                    className={styles.photoPreview} 
                  />
                  <button 
                    type="button" 
                    className={styles.removePhotoButton}
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview('');
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <label className={styles.photoUploadArea}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className={styles.fileInput}
                  />
                  <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
                  <div className={styles.uploadText}>
                    <p>Drop your image here or click to browse</p>
                    <span className={styles.uploadInfo}>
                      Max size: 5MB • Recommended: 1024×1024px
                    </span>
                  </div>
                </label>
              )}
              {formErrors.photo && (
                <p className={styles.errorText}>{formErrors.photo}</p>
              )}
            </div>
          </div>
          
          {/* Details Section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Equipment Details</h3>
            
            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.formLabel}>Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`${styles.formInput} ${formErrors.name ? styles.inputError : ''}`}
                placeholder="Enter equipment name"
              />
              {formErrors.name && (
                <p className={styles.errorText}>{formErrors.name}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="type" className={styles.formLabel}>Type</label>
              <select
                id="type"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setSubType('');
                  setBrand('');
                }}
                className={`${styles.formSelect} ${formErrors.type ? styles.inputError : ''}`}
              >
                <option value="">Select Type</option>
                {typeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.type && (
                <p className={styles.errorText}>{formErrors.type}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="subType" className={styles.formLabel}>Sub Type</label>
              <select
                id="subType"
                value={subType}
                onChange={(e) => setSubType(e.target.value)}
                className={`${styles.formSelect} ${formErrors.subType ? styles.inputError : ''}`}
                disabled={!type}
              >
                <option value="">Select Sub Type</option>
                {type && subTypeOptions[type]?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.subType && (
                <p className={styles.errorText}>{formErrors.subType}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="brand" className={styles.formLabel}>Brand</label>
              <select
                id="brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className={`${styles.formSelect} ${formErrors.brand ? styles.inputError : ''}`}
                disabled={!type}
              >
                <option value="">Select Brand</option>
                {type && brandOptions[type]?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.brand && (
                <p className={styles.errorText}>{formErrors.brand}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.formLabel}>Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={styles.formTextarea}
                placeholder="Enter equipment description"
                maxLength={1000}
              />
              <p className={styles.charCount}>
                {description.length}/1000 characters
              </p>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="quantity" className={styles.formLabel}>Quantity</label>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className={`${styles.formInput} ${formErrors.quantity ? styles.inputError : ''}`}
                  placeholder="Enter quantity"
                  min="1"
                />
                {formErrors.quantity && (
                  <p className={styles.errorText}>{formErrors.quantity}</p>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="rentPerDay" className={styles.formLabel}>Rent per Day (₹)</label>
                <input
                  type="number"
                  id="rentPerDay"
                  value={rentPerDay}
                  onChange={(e) => setRentPerDay(e.target.value)}
                  className={`${styles.formInput} ${formErrors.rentPerDay ? styles.inputError : ''}`}
                  placeholder="Enter rent per day"
                  min="0"
                  step="0.01"
                />
                {formErrors.rentPerDay && (
                  <p className={styles.errorText}>{formErrors.rentPerDay}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Attachment Section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Attachment (Optional)</h3>
            {attachmentName ? (
              <div className={styles.attachmentPreview}>
                <FontAwesomeIcon icon={faFilePdf} className={styles.pdfIcon} />
                <span className={styles.attachmentName}>{attachmentName}</span>
                <button 
                  type="button" 
                  className={styles.removeAttachmentButton}
                  onClick={() => {
                    setAttachmentFile(null);
                    setAttachmentName('');
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ) : (
              <label className={styles.attachmentUploadArea}>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleAttachmentChange}
                  className={styles.fileInput}
                />
                <FontAwesomeIcon icon={faFilePdf} className={styles.uploadIcon} />
                <div className={styles.uploadText}>
                  <p>Drop your PDF here or click to browse</p>
                  <span className={styles.uploadInfo}>
                    Maximum file size: 5MB
                  </span>
                </div>
              </label>
            )}
            {formErrors.attachment && (
              <p className={styles.errorText}>{formErrors.attachment}</p>
            )}
          </div>
          
          {/* Form Actions */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
                  <span>{uploadProgress ? `Uploading ${uploadProgress}%` : 'Saving...'}</span>
                </>
              ) : (
                <span>{editItem ? 'Update Equipment' : 'Add Equipment'}</span>
              )}
            </button>
          </div>
          
          {formErrors.submit && (
            <p className={styles.submitError}>{formErrors.submit}</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddVisualEquipmentModal;